// BS needs to be imported before React and other imports:
// https://docs.bugsnag.com/platforms/browsers/react/#bundled
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import React from 'react';

import TptConnectError from 'dpl/shared/fetching/utils/TptConnectError';
import { pick } from 'dpl/shared/utils';

// eslint-disable-next-line
let BugsnagErrorBoundary = ({ children }) => children;

// HACK: BS degrades performance signficantly on /explore pages. not sure for
// cause but could be related to the page's heavy mem consumption, or frequent
// pushes to history state.
const isExplorePage = window.location.pathname.startsWith('/explore');

// `main-B43LycMX.js` (Vite) or `main.8f3a1c2d.js` (webpack) -> `main.js`
function normalizeFile(url) {
  return url.pathname.replace(/[-.][\w-]{8,}(\.\w+)$/, '$1');
}

// Where a frame's script came from, with a label that is stable across
// deploys and pages: our CDN bundles get their de-hashed path, scripts in
// our HTML get `inline` whatever the page, third parties get their hostname,
// or the URL scheme when there is no host (`user-script:369` from iOS
// content blockers, where the number changes per injection). Null for frames
// with no script location (`[native code]`, `Array.map`, empty), which sit on
// top of many stacks and say nothing about origin.
function frameOrigin(frame) {
  let url;
  try {
    url = new URL(frame.file);
  } catch (err) {
    return null;
  }

  if (url.hostname === window.__DPL_ASSET_HOST) {
    return { kind: 'bundle', label: normalizeFile(url) };
  }

  if (url.hostname === window.__DPL_HOST) {
    return { kind: 'inline', label: 'inline' };
  }

  return {
    kind: 'foreign',
    label: url.hostname || url.protocol.slice(0, -1)
  };
}

function frameOrigins(stacktrace) {
  return stacktrace.map(frameOrigin).filter(Boolean);
}

export function isThirdPartyScript(event) {
  try {
    const [origin] = frameOrigins(event.errors[0].stacktrace);
    return Boolean(origin) && origin.kind === 'foreign';
  } catch (err) {
    return false;
  }
}

// Non-OK responses from our own server (mostly 422 validation failures, plus
// 401/428/429) are outcomes the server chose, not client bugs: the client
// handles them with toasts and form errors, and the server reports 5xx
// itself. The fetch layer raises a TptConnectError carrying the response for
// each one. ~32k/month of these reached Bugsnag through unhandled
// `mutateAsync` rejections and catch-all `notify(error)` calls.
function isServerResponse(event) {
  const { originalError } = event;

  return (
    originalError instanceof TptConnectError && Boolean(originalError.response)
  );
}

// First onError callback. Bugsnag drops the event, without sending it, when
// a callback returns false.
export function shouldSendEvent(event) {
  return !isServerResponse(event);
}

// Bugsnag groups errors by the reported function name of the "significant"
// stack frame, but Vite/esbuild minification rotates those names on every
// build (`DB.render` one deploy, `SB.render` the next) and browsers word the
// same failure differently, so a single bug fragments into many low-count
// error groups. When we can extract a build- and browser-stable signature
// from the error, we group on it instead. Everything else (minified
// line/column/method, raw message text, content-hashed filenames) changes
// per deploy or per browser and must stay out of the hash.

// Extracts the trailing property name from a member expression like `Pe.id`
// or `window.webkit.messageHandlers[t].postMessage`. Leading identifiers are
// minified (they rotate every build) but property names are not, so only a
// property reached via a `.` is stable enough to group on.
function stablePropertyName(expression) {
  const segments = expression
    .replace(/^new\s+/, '')
    .replace(/\([^(]*\)$/, '')
    .split('.');

  if (segments.length < 2) {
    return null;
  }

  const property = segments.pop().replace(/\[[^\]]*\]$/, '');
  return /^[A-Za-z_$][\w$]*$/.test(property) ? property : null;
}

function propertyReadSignature(value, property) {
  return property && `${value}.${property}`;
}

function notCallableSignature(expression, kind) {
  const property = stablePropertyName(expression);
  return property && `${property} is not a ${kind}`;
}

const MESSAGE_MATCHERS = [
  // Chrome: Cannot read properties of undefined (reading 'id')
  {
    pattern:
      /^Cannot read propert(?:ies|y) of (undefined|null) \(reading '([^']+)'\)/,
    signature: ([, value, property]) => propertyReadSignature(value, property)
  },
  // Chrome <93 / Edge legacy: Cannot read property 'id' of undefined
  {
    pattern: /^Cannot read property '([^']+)' of (undefined|null)/,
    signature: ([, property, value]) => propertyReadSignature(value, property)
  },
  // Firefox: can't access property "id", Pe is undefined
  {
    pattern: /^can't access property "([^"]+)", .+ is (undefined|null)/,
    signature: ([, property, value]) => propertyReadSignature(value, property)
  },
  // Safari: undefined is not an object (evaluating 'Pe.id')
  {
    pattern: /^(undefined|null) is not an object \(evaluating '([^']+)'\)/,
    signature: ([, value, expression]) =>
      propertyReadSignature(value, stablePropertyName(expression))
  },
  // Chrome/Firefox: Pe.push is not a function / X is not a constructor.
  // Also matches Safari's longer form:
  //   t.filter is not a function. (In 't.filter(...)', 't.filter' is undefined)
  {
    pattern: /^(\S+) is not a (function|constructor)/,
    signature: ([, expression, kind]) => notCallableSignature(expression, kind)
  },
  // Safari: undefined is not a constructor (evaluating 'new window.google.maps.LatLng(e,o)')
  {
    pattern:
      /^(?:undefined|null) is not a (function|constructor) \(evaluating '([^']+)'\)/,
    signature: ([, kind, expression]) => notCallableSignature(expression, kind)
  },
  // Chrome/Firefox: _AutofillCallbackHandler is not defined
  // Mostly globals that in-app browsers and autofill extensions inject.
  {
    pattern: /^\S+ is not defined$/,
    signature: ([message]) => message
  },
  // Safari: Can't find variable: _AutofillCallbackHandler
  {
    pattern: /^Can't find variable: (\S+)$/,
    signature: ([, name]) => `${name} is not defined`
  },
  // Chrome: Failed to fetch / Safari: Load failed /
  // Firefox: NetworkError when attempting to fetch resource.
  {
    pattern:
      /^(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?)$/,
    signature: () => 'fetch failed'
  },
  // Chrome/Safari: Maximum call stack size exceeded / Firefox: too much recursion
  {
    pattern: /^(?:Maximum call stack size exceeded\.?|too much recursion)$/,
    signature: () => 'call stack exceeded'
  },
  // A SyntaxError in a bundle means the download was truncated or swapped
  // for an HTML page (flaky network, captive portal, ad blocker); the build
  // itself parses. Chrome: Unexpected end of input / Invalid or unexpected
  // token / Unexpected token '<'. Safari: Unexpected EOF / Unexpected end of
  // script. JSON.parse failures word themselves differently and don't match.
  {
    pattern:
      /^(?:Unexpected end of (?:input|script)|Unexpected EOF|Invalid or unexpected token|Unexpected token '<')/,
    signature: () => 'script parse failed'
  }
];

function canonicalizeMessage(message) {
  for (let i = 0; i < MESSAGE_MATCHERS.length; i += 1) {
    const { pattern, signature } = MESSAGE_MATCHERS[i];
    const match = message.match(pattern);
    const result = match && signature(match);
    if (result) {
      return result;
    }
  }
  return null;
}

export function computeGroupingHash(event) {
  try {
    const { errorClass, errorMessage, stacktrace } = event.errors[0];
    const signature = canonicalizeMessage(errorMessage);
    const origins = frameOrigins(stacktrace);

    const bundle = origins.find(origin => origin.kind === 'bundle');
    if (bundle) {
      // Our code: group only on a stable signature. Without one, Bugsnag's
      // default grouping is the safer choice.
      return signature
        ? `${errorClass} ${signature} in ${bundle.label}`
        : undefined;
    }

    if (origins.length === 0) {
      return undefined;
    }

    // Nothing of ours in the stack: inline scripts, extensions, in-app
    // browsers, third-party SDKs. Bugsnag would key these on the page URL or
    // an injection line number, giving a new group per page or visit. Group
    // on the message shape, falling back to the raw message; nobody acts on
    // these per occurrence.
    const message = signature || errorMessage.trim();
    return `${errorClass} ${message} in ${origins[0].label}`;
  } catch (err) {
    return undefined;
  }
}

function lowerSeverity(event, reason) {
  event.severity = 'warning';
  event.addMetadata('severityReason', 'reason', reason);
}

export function lowerSeverityIfNeeded(event) {
  if (event.severity !== 'error') {
    return;
  }

  if (window.__DPL_UNSUPPORTED_BROWSER) {
    lowerSeverity(event, 'Unsupported browser');
    return;
  }

  if (window.__DPL_HOST !== window.location.hostname) {
    lowerSeverity(event, 'Proxied request');
    return;
  }

  if (isThirdPartyScript(event)) {
    lowerSeverity(event, 'Third-party script');
  }
}

if (CONFIG.bugsnagKey && !isExplorePage) {
  if (!window.bugsnagClient) {
    window.bugsnagClient = Bugsnag.start({
      releaseStage: CONFIG.bugsnagReleaseStage,
      apiKey: CONFIG.bugsnagKey,
      appVersion: CONFIG.appVersion,
      plugins: [new BugsnagPluginReact(React)],
      user: {
        id: window.__DPL_CURRENT_USER_ID
      },
      context: window.__DPL_PAGE_INFO?.category,
      onError: [
        shouldSendEvent,
        event => {
          lowerSeverityIfNeeded(event);
          event.addMetadata('debugSessionId', window.__DPL_DEBUG_SESSION_ID);
          event.addMetadata(
            'network',
            'connection',
            pick(window.navigator.connection || {}, [
              'downlink',
              'effectiveType'
            ])
          );

          // Bugsnag doesn't surface groupingHash in its UI, so mirror it into
          // metadata to make the custom grouping auditable per event.
          const groupingHash = computeGroupingHash(event);
          if (groupingHash) {
            event.groupingHash = groupingHash;
            event.addMetadata('grouping', 'hash', groupingHash);
          }
        }
      ]
    });
  }

  window.addEventListener('offline', () => {
    Bugsnag.addMetadata('network', 'wentOffline', new Date().toISOString());
  });

  window.addEventListener('online', () => {
    Bugsnag.addMetadata('network', 'wentOnline', new Date().toISOString());
  });

  BugsnagErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);
}

export default BugsnagErrorBoundary;
